import React, { ReactNode } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { splitMonthByWeeks } from '@helpers/splitMonthByWeeks';
import { MonthSlot } from '@pages/Schedule/components/Month/MonthSlot';
import { useScheduleContext } from '@pages/Schedule/context/useScheduleContext';
import { calcAmountByProperty } from '@pages/Schedule/utils/calcAmountByProperty';
import { TEventTypes } from '@pages/Schedule/components/Slot/types';
import { IScheduleResponse } from '@api/schedule/types';
import { TAppointment } from '@api/appointments/types';
import { useFetchAllPhysiciansQuery } from '@api/physicians/physiciansAPI';
import { useFetchLocationBlackoutsQuery } from '@api/blackout/blackoutAPI';
import { getBlackourLocationForDate } from '@pages/Schedule/utils/getBlackoutsLocationForDate';

interface IMonthBodyProps {
  appointments: IScheduleResponse | undefined;
}

export const MonthBody = ({ appointments }: IMonthBodyProps) => {
  const { selectedDate, selectedLocationId } = useScheduleContext();

  const { data: AllPhysicians } = useFetchAllPhysiciansQuery();

  const { data: locationsBlackout } = useFetchLocationBlackoutsQuery(
    !selectedLocationId ? skipToken : selectedLocationId,
  );

  const tableData = splitMonthByWeeks(selectedDate);

  const scheduleData: TAppointment[] | undefined = appointments?.data;

  const tableBody = tableData.reduce<ReactNode[]>((acc, currentWeek, rowIndex) => {
    const tableRow = currentWeek.map(({ value }, colIndex) => {
      const currentDateAppointments = scheduleData
        ?.filter(({ locationId }) => locationId === selectedLocationId)
        .filter(({ date }) => isSameDay(new Date(date), value));

      const appointmentsAmount = currentDateAppointments?.length;

      const groupedAppointmentsByType = appointmentsAmount
        ? calcAmountByProperty(currentDateAppointments, 'type')
        : [];

      const blackoutsSchedule = AllPhysicians?.data?.results
        .filter((physician) => physician?.schedule?.locationId === selectedLocationId)
        .flatMap((physician) =>
          (physician?.blackout || []).map((blackout) => ({
            ...blackout,
            physicianName: physician.name,
          })),
        );

      const blackoutLocationsSlots = getBlackourLocationForDate(value, locationsBlackout?.data);

      const currentBlackoutLocation = blackoutLocationsSlots
        ?.filter(({ locationId }) => locationId === selectedLocationId)
        ?.find((slot) => {
          if (slot.from && slot.to && slot.startTime) {
            const fromDate = new Date(`${slot.from} ${slot.startTime}`);

            const toDate = new Date(`${slot.to} ${slot.to_time}`);

            return (
              isSameDay(fromDate, value) ||
              isSameDay(toDate, value) ||
              (isAfter(value, fromDate) && isBefore(value, toDate))
            );
          }

          return false;
        });

      const currentBlackout = blackoutsSchedule?.filter((slot) => {
        if (slot.from && slot.to) {
          const fromDate = new Date(slot.from);

          const toDate = new Date(slot.to);

          const fromTimeParts = slot.from_time.split(':').map(Number);

          const toTimeParts = slot.to_time.split(':').map(Number);

          fromDate.setHours(fromTimeParts[0], fromTimeParts[1], 0, 0);

          toDate.setHours(toTimeParts[0], toTimeParts[1], 59, 999);

          return (
            isSameDay(fromDate, value) ||
            isSameDay(toDate, value) ||
            (isAfter(value, fromDate) && isBefore(value, toDate))
          );
        }

        return false;
      });

      return (
        <MonthSlot
          key={`month-slot-${rowIndex}-${colIndex}`}
          date={value}
          appointmentsAmount={appointmentsAmount}
          blackoutsPhysician={currentBlackout}
          blackoutLocation={currentBlackoutLocation}
          eventTypes={groupedAppointmentsByType as TEventTypes[]}
        />
      );
    });

    return [
      ...acc,
      <div
        className="schedule-row table-row"
        key={`table-row-${rowIndex}`}
        style={{
          ['height' as string]: `${100 / tableData.length}%`,
        }}
      >
        {tableRow}
      </div>,
    ];
  }, []);

  return <div className="schedule-body table-body">{tableBody}</div>;
};
